import { useEffect, useState, useCallback } from 'react';
import '@/App.css';
import axios from 'axios';
import { Link } from 'react-router-dom';
import { SpinnerWheel } from '@/components/SpinnerWheel';
import { RecipeModal } from '@/components/RecipeModal';
import { DietaryFilters } from '@/components/DietaryFilters';
import { CustomWheelBuilder } from '@/components/CustomWheelBuilder';
import { MealPlanner } from '@/components/MealPlanner';
import { AdBanner } from '@/components/AdBanner';
import { RegionsDrawer } from '@/components/RegionsDrawer';
import { RecipeBot } from '@/components/RecipeBot';
import { DecideMyMeal } from '@/components/DecideMyMeal';
import { HomeShelves } from '@/components/HomeShelves';
import { DishImage } from '@/components/DishImage';
import { PairingResultModal } from '@/components/PairingResultModal';
import { CuisineLinks } from '@/components/CuisineLinks';
import { SiteFooter } from '@/components/SiteFooter';
import { POSTS } from '@/lib/blogPosts';
import { getPairingCandidates } from '@/lib/pairing';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Button } from '@/components/ui/button';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { toast } from 'sonner';
import { ChefHat, Wine, Utensils, Coffee, Pizza, Send, Shuffle, Settings2, CalendarDays, Cake, Flame, Menu } from 'lucide-react';
import Lenis from 'lenis';
import { useSEO, slugify, SITE_URL } from '@/lib/seo';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

function App() {
  const [recipes, setRecipes] = useState([]);
  const [selectedCategory, setSelectedCategory] = useState('breakfast');
  const [selectedDietary, setSelectedDietary] = useState('all');
  const [wheelItems, setWheelItems] = useState([]);
  const [allCategoryItems, setAllCategoryItems] = useState([]);
  const [accompanimentItems, setAccompanimentItems] = useState([]);
  const [selectedRecipe, setSelectedRecipe] = useState(null);
  const [showRecipeModal, setShowRecipeModal] = useState(false);
  const [showFeedbackModal, setShowFeedbackModal] = useState(false);
  const [showMealPlanner, setShowMealPlanner] = useState(false);
  const [showRegionsDrawer, setShowRegionsDrawer] = useState(false);
  const [lastSpinResult, setLastSpinResult] = useState(null);
  const [wheelMode, setWheelMode] = useState('preloaded');
  const [pairingResult, setPairingResult] = useState(null);
  const [showPairingResult, setShowPairingResult] = useState(false);
  const [feedbackForm, setFeedbackForm] = useState({
    name: '',
    email: '',
    recipe_name: '',
    recipe_description: '',
    category: 'recipe-submission'
  });

  // Initialize Lenis smooth scrolling
  useEffect(() => {
    const lenis = new Lenis({
      duration: 1.2,
      easing: (t) => Math.min(1, 1.001 - Math.pow(2, -10 * t)),
      smooth: true,
    });

    function raf(time) {
      lenis.raf(time);
      requestAnimationFrame(raf);
    }

    requestAnimationFrame(raf);

    return () => {
      lenis.destroy();
    };
  }, []);

  const fetchRecipes = useCallback(async () => {
    try {
      const response = await axios.get(`${API}/recipes`);
      const mainRecipes = response.data.filter(r => r.category !== 'accompaniment');
      setRecipes(mainRecipes);
    } catch (error) {
      if (process.env.NODE_ENV === 'development') {
        console.error('Error fetching recipes:', error);
      }
      toast.error('Failed to load recipes');
    }
  }, []);

  const fetchAccompaniments = useCallback(async () => {
    try {
      const response = await axios.get(`${API}/recipes?category=accompaniment`);
      setAccompanimentItems(response.data);
    } catch (error) {
      if (process.env.NODE_ENV === 'development') {
        console.error('Error fetching accompaniments:', error);
      }
    }
  }, []);

  // Fetch recipes on mount
  useEffect(() => {
    fetchRecipes();
    fetchAccompaniments();
  }, [fetchRecipes, fetchAccompaniments]);

  // Filter recipes when category/dietary changes
  useEffect(() => {
    // Base (non-regional) recipes are the "everyday" set
    const categoryAll = recipes.filter((r) => r.category === selectedCategory && !r.region);
    setAllCategoryItems(categoryAll);

    let quick = categoryAll;
    if (selectedDietary !== 'all') {
      quick = quick.filter((recipe) => recipe.dietary_tags.includes(selectedDietary));
    }
    setWheelItems(quick);
  }, [selectedCategory, selectedDietary, recipes]);

  const handleSpinComplete = useCallback((recipe) => {
    setSelectedRecipe(recipe);
    setLastSpinResult(recipe);
    setShowRecipeModal(true);
  }, []);

  const handleAccompanimentSpinComplete = useCallback((accompaniment) => {
    setPairingResult(accompaniment);
    setShowPairingResult(true);
  }, []);

  const handleFeedbackSubmit = useCallback(async (e) => {
    e.preventDefault();
    try {
      const response = await axios.post(`${API}/feedback`, feedbackForm);
      toast.success(response.data.message);
      setShowFeedbackModal(false);
      setFeedbackForm({
        name: '',
        email: '',
        recipe_name: '',
        recipe_description: '',
        category: 'recipe-submission'
      });
    } catch (error) {
      if (process.env.NODE_ENV === 'development') {
        console.error('Error submitting feedback:', error);
      }
      toast.error('Failed to submit feedback');
    }
  }, [feedbackForm]);

  const crazyDishes = recipes.filter((recipe) => recipe.category === 'crazy');

  // SEO: homepage structured data
  const homeJsonLd = {
    "@context": "https://schema.org",
    "@graph": [
      {
        "@type": "WebSite",
        "@id": `${SITE_URL}/#website`,
        "url": `${SITE_URL}/`,
        "name": "Masala Spin",
        "description": "Authentic Indian recipes discovered by spinning the wheel",
        "publisher": { "@type": "Organization", "name": "Masala Spin" },
      },
      {
        "@type": "ItemList",
        "itemListElement": recipes.slice(0, 50).map((r, i) => ({
          "@type": "ListItem",
          "position": i + 1,
          "url": `${SITE_URL}/recipe/${slugify(r.name)}`,
          "name": r.name,
        })),
      },
    ],
  };

  useSEO({
    title: "Masala Spin — Can't decide what to cook? Spin the wheel for Indian recipes.",
    description: "Spin the wheel and discover authentic Indian recipes across Maharashtra, Goa, Kerala and more. Weekly meal planner, 21-day weight loss challenge, AI chef bot.",
    canonical: `${SITE_URL}/`,
    jsonLd: homeJsonLd,
  });

  return (
    <div className="min-h-screen" style={{ background: '#FDFBF7' }}>
      {/* Noise Texture Overlay */}
      <div className="fixed inset-0 pointer-events-none opacity-20 mix-blend-overlay" 
           style={{ backgroundImage: 'url("data:image/svg+xml,%3Csvg viewBox=\'0 0 400 400\' xmlns=\'http://www.w3.org/2000/svg\'%3E%3Cfilter id=\'noiseFilter\'%3E%3CfeTurbulence type=\'fractalNoise\' baseFrequency=\'0.9\' numOctaves=\'4\' stitchTiles=\'stitch\'/%3E%3C/filter%3E%3Crect width=\'100%25\' height=\'100%25\' filter=\'url(%23noiseFilter)\'/%3E%3C/svg%3E")' }} 
      />

      {/* Header */}
      <header className="sticky top-0 z-40 bg-white/70 backdrop-blur-2xl border-b border-white/40 shadow-lg backdrop-saturate-150">
        <div className="max-w-7xl mx-auto px-6 py-4 flex justify-between items-center">
          <div className="flex items-center gap-3">
            <button
              onClick={() => setShowRegionsDrawer(true)}
              data-testid="header-regions-hamburger"
              aria-label="Open regions menu"
              className="p-2 rounded-lg hover:bg-[#1C1917]/5 transition-colors"
            >
              <Menu className="w-6 h-6 text-[#1C1917]" />
            </button>
            <ChefHat className="w-8 h-8 text-[#F97316]" />
            <span className="font-['Playfair_Display'] text-2xl sm:text-3xl font-bold text-[#1C1917] tracking-tight">
              Masala Spin
            </span>
          </div>
          <div className="flex items-center gap-2 sm:gap-3">
            <nav aria-label="Primary" className="hidden lg:block mr-2">
              <ul className="flex items-center gap-5 font-['Outfit'] text-sm">
                <li><a href="#meal-spinner" data-testid="home-nav-recipes" className="text-[#1C1917]/70 hover:text-[#F97316] transition-colors">Recipes</a></li>
                <li><a href="#cuisines" data-testid="home-nav-cuisines" className="text-[#1C1917]/70 hover:text-[#F97316] transition-colors">Cuisines</a></li>
                <li><Link to="/blog" data-testid="home-nav-blog" className="text-[#1C1917]/70 hover:text-[#F97316] transition-colors">Recipe Blog</Link></li>
                <li><Link to="/about" data-testid="home-nav-about" className="text-[#1C1917]/70 hover:text-[#F97316] transition-colors">About</Link></li>
              </ul>
            </nav>
            <Link
              to="/challenge"
              data-testid="header-challenge-link"
              className="flex items-center gap-1.5 sm:gap-2 px-3 sm:px-5 py-2 sm:py-2.5 bg-gradient-to-r from-[#F97316] to-[#FBBF24] text-white rounded-full font-['Outfit'] font-medium text-xs sm:text-base hover:shadow-xl hover:-translate-y-0.5 transform transition-all duration-300 whitespace-nowrap"
            >
              <Flame className="w-3.5 h-3.5 sm:w-4 sm:h-4" />
              <span className="hidden xs:inline sm:inline">21-Day</span>
              <span className="hidden sm:inline">Challenge</span>
            </Link>
            <button
              onClick={() => setShowMealPlanner(true)}
              data-testid="open-meal-planner-button"
              className="px-3 sm:px-6 py-2 sm:py-2.5 bg-[#2D4A22] text-white rounded-full font-['Outfit'] font-medium text-xs sm:text-base hover:bg-[#1C1917] transition-colors shadow-lg hover:shadow-xl hover:-translate-y-0.5 transform duration-300 flex items-center gap-1.5 sm:gap-2 whitespace-nowrap"
            >
              <CalendarDays className="w-3.5 h-3.5 sm:w-4 sm:h-4" />
              <span className="hidden xs:inline">Meal Planner</span>
              <span className="xs:hidden">Plan</span>
            </button>
            <button
              onClick={() => setShowFeedbackModal(true)}
              data-testid="open-feedback-button"
              className="px-3 sm:px-6 py-2 sm:py-2.5 bg-[#F97316] text-white rounded-full font-['Outfit'] font-medium text-xs sm:text-base hover:bg-[#EA580C] transition-colors shadow-lg hover:shadow-xl hover:-translate-y-0.5 transform duration-300 whitespace-nowrap"
            >
              <span className="hidden xs:inline">Submit Recipe</span>
              <span className="xs:hidden">Submit</span>
            </button>
          </div>
        </div>
      </header>

      <main>
      {/* NEW HERO: Decide My Meal */}
      <DecideMyMeal
        recipes={recipes}
        onOpenRecipe={(r) => {
          setSelectedRecipe(r);
          setShowRecipeModal(true);
        }}
      />

      {/* Meal Spinner Section */}
      <section id="meal-spinner" className="py-20 px-6" style={{ background: 'linear-gradient(180deg, #FDFBF7 0%, #FEF3E2 100%)' }}>
        <div className="max-w-6xl mx-auto">
          <div className="text-center mb-12">
            <h2 className="font-['Playfair_Display'] text-4xl sm:text-5xl font-bold text-[#1C1917] tracking-tight mb-4">
              Spin Your Meal
            </h2>
            <p className="text-base text-[#1C1917]/60 font-['Outfit'] max-w-2xl mx-auto leading-relaxed">
              Choose your meal type, apply dietary filters, and let the wheel decide!
            </p>
          </div>

          {/* Meal Category Selection */}
          <div className="flex flex-wrap gap-4 justify-center mb-8">
            {[
              { value: 'breakfast', label: 'Breakfast', icon: Coffee },
              { value: 'lunch', label: 'Lunch', icon: Utensils },
              { value: 'dinner', label: 'Dinner', icon: ChefHat },
              { value: 'drinks', label: 'Drinks', icon: Wine },
              { value: 'desserts', label: 'Desserts', icon: Cake },
            ].map((category) => {
              const Icon = category.icon;
              const isActive = selectedCategory === category.value;
              return (
                <button
                  key={category.value}
                  onClick={() => setSelectedCategory(category.value)}
                  data-testid={`category-${category.value}`}
                  className={`px-8 py-4 rounded-full font-['Outfit'] font-semibold text-lg transition-all duration-300 flex items-center gap-3 ${
                    isActive
                      ? 'bg-gradient-to-r from-[#F97316] to-[#FBBF24] text-white shadow-xl scale-105'
                      : 'bg-white text-[#1C1917] hover:bg-white/80 shadow-md border border-[#1C1917]/10'
                  }`}
                >
                  <Icon className="w-5 h-5" />
                  {category.label}
                </button>
              );
            })}
          </div>

          {/* Dietary Filters */}
          <div className="mb-8">
            <DietaryFilters selectedDietary={selectedDietary} onDietaryChange={setSelectedDietary} />
          </div>

          {/* Wheel Mode Tabs */}
          <Tabs value={wheelMode} onValueChange={setWheelMode} className="w-full">
            <TabsList className="grid w-full max-w-md mx-auto grid-cols-2 mb-8 bg-white/60 backdrop-blur-md rounded-full p-1">
              <TabsTrigger 
                value="preloaded" 
                data-testid="preloaded-wheel-tab"
                className="rounded-full data-[state=active]:bg-[#F97316] data-[state=active]:text-white font-['Outfit'] font-medium"
              >
                <Shuffle className="w-4 h-4 mr-2" />
                Quick Spin
              </TabsTrigger>
              <TabsTrigger 
                value="custom"
                data-testid="custom-wheel-tab"
                className="rounded-full data-[state=active]:bg-[#F97316] data-[state=active]:text-white font-['Outfit'] font-medium"
              >
                <Settings2 className="w-4 h-4 mr-2" />
                Build My Wheel
              </TabsTrigger>
            </TabsList>

            {/* Pre-loaded Wheel */}
            <TabsContent value="preloaded">
              <div className="flex flex-col items-center gap-8">
                {wheelItems.length > 0 ? (
                  <SpinnerWheel items={wheelItems} onSpinComplete={handleSpinComplete} size={400} />
                ) : (
                  <div className="text-center py-12">
                    <p className="text-xl font-['Outfit'] text-[#1C1917]/60">
                      No recipes found for selected filters. Try different options!
                    </p>
                  </div>
                )}
              </div>
            </TabsContent>

            {/* Custom Wheel Builder */}
            <TabsContent value="custom">
              <CustomWheelBuilder 
                availableItems={allCategoryItems}
                onSpinComplete={handleSpinComplete}
                category={selectedCategory}
              />
            </TabsContent>
          </Tabs>

          {/* Accompaniment Wheel — only when last spin was lunch/dinner AND pairing makes sense */}
          {(selectedCategory === 'lunch' || selectedCategory === 'dinner') && (() => {
            const pairingItems = getPairingCandidates(lastSpinResult, accompanimentItems);
            if (!lastSpinResult || pairingItems.length === 0) return null;
            return (
              <div className="mt-16 pt-12 border-t border-[#1C1917]/10">
                <div className="text-center mb-8">
                  <h3 className="font-['Playfair_Display'] text-3xl font-bold text-[#1C1917] tracking-tight mb-3">
                    What to eat <span className="italic">{lastSpinResult.name}</span> with?
                  </h3>
                  <p className="text-sm text-[#1C1917]/60 font-['Outfit']">
                    Spin to pick the perfect side for your meal.
                  </p>
                </div>
                <div className="flex justify-center">
                  <SpinnerWheel
                    items={pairingItems}
                    onSpinComplete={handleAccompanimentSpinComplete}
                    size={320}
                  />
                </div>
              </div>
            );
          })()}
        </div>
      </section>

      {/* Today's Picks · Quick Meals (Tired meals removed - duplicate) */}
      <HomeShelves
        recipes={recipes}
        onOpen={(r) => {
          setSelectedRecipe(r);
          setShowRecipeModal(true);
        }}
      />

      {/* Ad slot — renders only when an AdSense unit is configured */}
      <AdBanner slot="leaderboard" className="my-6" />

      {/* Regional Cuisine — moved into header hamburger drawer */}

      {/* Regional cuisine index — crawlable links into every regional recipe */}
      <CuisineLinks recipes={recipes} />

      {/* Crazy Dishes Section */}
      <section id="crazy-dishes" className="py-20 px-6 bg-[#2D4A22] relative overflow-hidden">
        <div className="max-w-7xl mx-auto relative z-10">
          <div className="text-center mb-12">
            <h2 className="font-['Playfair_Display'] text-4xl sm:text-5xl font-bold text-[#FDFBF7] tracking-tight mb-4">
              Crazy Fusion Dishes
            </h2>
            <p className="text-base text-[#FDFBF7]/80 font-['Outfit'] max-w-2xl mx-auto leading-relaxed">
              Experience the best of both worlds with our Indo-Western fusion creations!
            </p>
          </div>

          {/* Fusion Grid — all 18 dishes, click any to view recipe */}
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
            {crazyDishes.map((dish, index) => (
              <button
                key={dish.id}
                onClick={() => {
                  setSelectedRecipe(dish);
                  setShowRecipeModal(true);
                }}
                data-testid={`crazy-dish-${index}`}
                className="group relative rounded-3xl overflow-hidden text-left h-[260px] bg-gradient-to-br from-[#F97316] via-[#FBBF24] to-[#2D4A22] hover:-translate-y-1 hover:shadow-2xl transition-all duration-500"
              >
                {dish.image_url ? (
                  <DishImage
                    src={dish.image_url}
                    alt={dish.name}
                    fallbackInitial={dish.name?.[0] || 'M'}
                    className="absolute inset-0 w-full h-full"
                    imgClassName="absolute inset-0 w-full h-full object-cover group-hover:scale-105 transition-transform duration-700"
                  />
                ) : (
                  <div className="absolute inset-0 flex items-center justify-center">
                    <span className="font-['Playfair_Display'] text-7xl font-bold text-white/25 select-none">
                      {dish.name?.[0] || 'M'}
                    </span>
                  </div>
                )}
                <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-black/40 to-transparent" />
                <div className="absolute bottom-0 left-0 right-0 p-5">
                  <h3 className="font-['Playfair_Display'] text-xl font-bold text-white mb-1.5 leading-tight">
                    {dish.name}
                  </h3>
                  <div className="flex items-center gap-2 text-white/85 text-xs font-['Outfit']">
                    <span>{dish.calories} cal</span>
                    <span>•</span>
                    <span>{dish.prep_time}</span>
                    <span className="ml-auto px-2 py-0.5 rounded-full bg-white/20 backdrop-blur-sm text-white/90 text-[10px]">
                      View recipe →
                    </span>
                  </div>
                </div>
                <div className="absolute top-4 right-4">
                  <Pizza className="w-5 h-5 text-[#FBBF24] drop-shadow" />
                </div>
              </button>
            ))}
          </div>
        </div>
      </section>

      {/* Recipe blog — internal links to long-form guides */}
      <section id="guides" className="py-20 px-6" style={{ background: '#FEF3E2' }}>
        <div className="max-w-6xl mx-auto">
          <div className="max-w-2xl mb-10">
            <p className="text-xs uppercase tracking-widest text-[#F97316] font-['Outfit'] font-semibold mb-3">
              From the recipe blog
            </p>
            <h2 className="font-['Playfair_Display'] text-4xl sm:text-5xl font-bold text-[#1C1917] tracking-tight mb-4">
              Cooking guides, not just recipes
            </h2>
            <p className="text-base text-[#1C1917]/60 font-['Outfit'] leading-relaxed">
              Longer reads that group our recipes by region, grain or occasion — with times,
              ingredients and a link to every dish mentioned.
            </p>
          </div>
          <ul className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
            {POSTS.map((p) => (
              <li key={p.slug}>
                <Link
                  to={`/blog/${p.slug}`}
                  data-testid={`home-guide-${p.slug}`}
                  className="block h-full p-5 rounded-2xl bg-white border border-[#1C1917]/5 hover:border-[#F97316]/40 hover:shadow-lg transition-all"
                >
                  <h3 className="font-['Playfair_Display'] text-lg font-bold text-[#1C1917] mb-2 leading-tight">
                    {p.title}
                  </h3>
                  <p className="text-sm text-[#1C1917]/60 font-['Outfit'] leading-relaxed line-clamp-3">
                    {p.description}
                  </p>
                  <p className="mt-3 text-xs text-[#1C1917]/45 font-['Outfit']">
                    {p.readMinutes} min read
                  </p>
                </Link>
              </li>
            ))}
          </ul>
        </div>
      </section>

      {/* Ad slot above the footer — renders only when configured */}
      <AdBanner slot="banner" className="my-8" />

      </main>

      {/* Footer */}
      <SiteFooter />

      {/* Recipe Modal */}
      <RecipeModal recipe={selectedRecipe} open={showRecipeModal} onClose={() => setShowRecipeModal(false)} />

      {/* Feedback Modal */}
      <Dialog open={showFeedbackModal} onOpenChange={setShowFeedbackModal}>
        <DialogContent className="max-w-md bg-white/90 backdrop-blur-xl" data-testid="feedback-modal">
          <DialogHeader>
            <DialogTitle className="font-['Playfair_Display'] text-2xl font-bold text-[#1C1917]">
              Share Your Recipe
            </DialogTitle>
          </DialogHeader>
          <form onSubmit={handleFeedbackSubmit} className="space-y-4 font-['Outfit']">
            <div>
              <Input
                placeholder="Your Name"
                value={feedbackForm.name}
                onChange={(e) => setFeedbackForm({ ...feedbackForm, name: e.target.value })}
                required
                data-testid="feedback-name-input"
                className="border-[#1C1917]/20 focus:border-[#F97316]"
              />
            </div>
            <div>
              <Input
                type="email"
                placeholder="Your Email"
                value={feedbackForm.email}
                onChange={(e) => setFeedbackForm({ ...feedbackForm, email: e.target.value })}
                required
                data-testid="feedback-email-input"
                className="border-[#1C1917]/20 focus:border-[#F97316]"
              />
            </div>
            <div>
              <Input
                placeholder="Recipe Name"
                value={feedbackForm.recipe_name}
                onChange={(e) => setFeedbackForm({ ...feedbackForm, recipe_name: e.target.value })}
                required
                data-testid="feedback-recipe-name-input"
                className="border-[#1C1917]/20 focus:border-[#F97316]"
              />
            </div>
            <div>
              <Textarea
                placeholder="Recipe Description & Instructions"
                value={feedbackForm.recipe_description}
                onChange={(e) => setFeedbackForm({ ...feedbackForm, recipe_description: e.target.value })}
                required
                rows={4}
                data-testid="feedback-recipe-description-input"
                className="border-[#1C1917]/20 focus:border-[#F97316]"
              />
            </div>
            <Button
              type="submit"
              data-testid="submit-feedback-button"
              className="w-full bg-[#F97316] hover:bg-[#EA580C] text-white font-semibold py-3 rounded-full transition-colors"
            >
              <Send className="w-4 h-4 mr-2" />
              Submit Recipe
            </Button>
          </form>
        </DialogContent>
      </Dialog>

      {/* Meal Planner Modal */}
      <Dialog open={showMealPlanner} onOpenChange={setShowMealPlanner}>
        <DialogContent className="max-w-5xl max-h-[90vh] p-0 bg-transparent border-none shadow-none overflow-auto" data-testid="meal-planner-modal">
          <MealPlanner lastSpinResult={lastSpinResult} onClose={() => setShowMealPlanner(false)} />
        </DialogContent>
      </Dialog>

      {/* Floating AI Recipe Bot */}
      <RecipeBot />

      {/* Smart pairing celebration popup */}
      <PairingResultModal
        open={showPairingResult}
        mainDish={lastSpinResult}
        accompaniment={pairingResult}
        onClose={() => setShowPairingResult(false)}
      />

      {/* Regions Drawer (header hamburger) */}
      <RegionsDrawer
        open={showRegionsDrawer}
        onClose={() => setShowRegionsDrawer(false)}
        onSpinComplete={handleSpinComplete}
      />
    </div>
  );
}

export default App;
